import { useState } from "react";
import { Check } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import FadeIn from "../components/FadeIn";
import SEO from "../components/SEO";

const SURVEY_URL = process.env.REACT_APP_SURVEY_URL;

const TIERS = {
  free: {
    label: "Free",
    headline: "Start for free",
    body: [
      "You don't need to commit to anything to join HAEVN.",
      "Complete your survey and see your matches first.\nIf it's working for you, you can decide to join.",
    ],
    rightLabel: "What you can do",
    features: [
      "Complete your survey",
      "See your matches",
      "Preview profiles",
      "Stay in the network",
    ],
    cta: "Start Your Survey",
  },
  six: {
    label: "6 Months",
    badge: "HAEVN+",
    headline: "6 months is the sweet spot",
    body: [
      "Most people are used to thinking in monthly subscriptions.\nThat's not how real connection works.",
      "Meaningful connections take time.\nNot days. Not a few swipes.",
      "Six months gives the network time to work.\nTo surface the right people. To create real opportunities.",
      "It also removes the pressure of a recurring subscription.\nYou're not paying indefinitely. You're choosing a window to engage.",
    ],
    price: "$199",
    priceSub: "total",
    monthly: "~$33/month",
    rightLabel: "What membership unlocks",
    features: [
      "Connect with your matches",
      "View full profiles and photos",
      "Participate fully in the network",
      "Continue receiving new matches",
    ],
    cta: "Join HAEVN",
  },
  twelve: {
    label: "12 Months",
    badge: "HAEVN+",
    headline: "For ongoing participation",
    body: [
      "Some people want to stay active in the network over time.",
      "If you're open to multiple connections, non-traditional structures, or simply want more opportunities as they appear, a longer window makes sense.",
      "This keeps you in the network as new matches continue to surface.",
    ],
    price: "$299",
    priceSub: "total",
    monthly: "~$25/month",
    rightLabel: "Everything included",
    features: [
      "Full network access",
      "Ongoing matches as they appear",
      "Extended participation in the network",
    ],
    cta: "Join HAEVN",
  },
};

export default function PricingPage() {
  const [tier, setTier] = useState("free");
  const t = TIERS[tier];

  return (
    <div className="min-h-screen bg-white" data-testid="pricing-page">
      <SEO
        title="Pricing"
        path="/pricing"
        description="Join HAEVN for free. See your matches before you pay. Membership plans start at $199 for 6 months."
      />
      <Navigation />

      <section className="pt-24 pb-20 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          {/* Headline above selector */}
          <FadeIn>
            <p className="text-xs font-subhead uppercase tracking-widest text-gray-400 mb-4">Membership Plans</p>
            <h1 className="font-headline text-4xl sm:text-5xl lg:text-6xl text-haevn-navy leading-tight mb-5">
              One membership. No subscriptions.
            </h1>
            <div className="max-w-[640px]">
              <p className="text-base text-gray-600 font-body leading-relaxed mb-1">
                You don't need to commit to join HAEVN.
              </p>
              <p className="text-base text-gray-500 font-body leading-relaxed">
                Start for free. See your matches. Then decide.
              </p>
            </div>
          </FadeIn>

          {/* Selector */}
          <div className="flex items-center gap-2 mt-8 mb-14" data-testid="tier-toggle">
            <button
              onClick={() => setTier("free")}
              className={`px-6 py-2.5 rounded-full text-sm font-subhead transition-all duration-200 ${
                tier === "free"
                  ? "bg-haevn-navy text-white"
                  : "bg-gray-100 text-gray-500 hover:text-haevn-navy"
              }`}
              data-testid="tier-free"
            >
              Free
            </button>
            {tier === "free" ? (
              <button
                onClick={() => setTier("six")}
                className="px-6 py-2.5 rounded-full text-sm font-subhead bg-gray-100 text-gray-500 hover:text-haevn-navy transition-all duration-200"
                data-testid="tier-plus-collapsed"
              >
                HAEVN+
              </button>
            ) : (
              <div className="flex items-center gap-1 border border-gray-200 rounded-full pl-4 pr-1 py-1 animate-fadeIn">
                <span className="text-xs font-subhead text-haevn-teal uppercase tracking-wide mr-2">HAEVN+</span>
                <button
                  onClick={() => setTier("six")}
                  className={`px-5 py-1.5 rounded-full text-sm font-subhead transition-all duration-200 ${
                    tier === "six"
                      ? "bg-haevn-navy text-white"
                      : "text-gray-500 hover:text-haevn-navy"
                  }`}
                  data-testid="tier-six"
                >
                  6 Months
                </button>
                <button
                  onClick={() => setTier("twelve")}
                  className={`px-5 py-1.5 rounded-full text-sm font-subhead transition-all duration-200 ${
                    tier === "twelve"
                      ? "bg-haevn-navy text-white"
                      : "text-gray-500 hover:text-haevn-navy"
                  }`}
                  data-testid="tier-twelve"
                >
                  12 Months
                </button>
              </div>
            )}
          </div>

          {/* Two-column layout */}
          <div
            key={tier}
            className="grid md:grid-cols-2 gap-0 rounded-lg overflow-hidden border border-gray-200 animate-fadeIn"
            data-testid="pricing-panel"
          >
            {/* LEFT — Why */}
            <div className="bg-[#F3F2F0] p-8 md:p-12 flex flex-col">
              <h2 className="font-headline text-3xl md:text-4xl text-haevn-navy mb-6 leading-tight">
                {t.headline}
              </h2>
              <div className="space-y-4 flex-1">
                {t.body.map((block, i) => (
                  <div key={i}>
                    {block.split("\n").map((line, j) => (
                      <p key={j} className="text-base text-gray-600 font-body leading-relaxed">
                        {line}
                      </p>
                    ))}
                  </div>
                ))}
              </div>
              <div className="mt-10">
                <a
                  href={SURVEY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-haevn-navy text-white px-8 py-3.5 font-subhead text-sm hover:bg-haevn-navy/90 transition-all duration-200"
                  data-testid="pricing-cta-btn"
                >
                  {t.cta}
                </a>
              </div>
            </div>

            {/* RIGHT — Access */}
            <div className="bg-white p-8 md:p-12 flex flex-col">
              {/* Price (only for paid tiers) */}
              {t.price && (
                <div className="mb-8">
                  <p className="text-sm text-gray-400 font-body mb-1">{t.monthly}</p>
                  <p className="font-body text-5xl md:text-6xl text-gray-400 leading-none">
                    {t.price}
                  </p>
                  <p className="text-sm text-gray-400 font-body mt-1">{t.priceSub}</p>
                </div>
              )}

              {/* Features */}
              <div className={t.price ? "border-t border-gray-200 pt-8" : ""}>
                <p className="text-sm font-subhead text-gray-400 uppercase tracking-wide mb-6">
                  {t.rightLabel}
                </p>
                <div className="space-y-5">
                  {t.features.map((f, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-haevn-teal flex-shrink-0" />
                      <span className="text-base text-gray-700 font-body">{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
